import { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const ClubContext = createContext(null);

// Helper to convert hex to RGB for rgba() usage
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Apply club colors as CSS variables
const applyClubColors = (primaryColor, secondaryColor) => {
  const root = document.documentElement;
  
  // Default colors if not set
  const primary = primaryColor || '#1e3a5f';
  const secondary = secondaryColor || '#ce1126';
  
  // Set CSS variables
  root.style.setProperty('--club-primary', primary);
  root.style.setProperty('--club-secondary', secondary);
  
  // Create lighter/darker variants
  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  
  if (primaryRgb) {
    root.style.setProperty('--club-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    root.style.setProperty('--club-primary-light', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`);
    root.style.setProperty('--club-primary-dark', `rgba(${Math.max(0, primaryRgb.r - 30)}, ${Math.max(0, primaryRgb.g - 30)}, ${Math.max(0, primaryRgb.b - 30)}, 1)`);
  }
  
  if (secondaryRgb) {
    root.style.setProperty('--club-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    root.style.setProperty('--club-secondary-light', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)`);
    root.style.setProperty('--club-secondary-dark', `rgba(${Math.max(0, secondaryRgb.r - 30)}, ${Math.max(0, secondaryRgb.g - 30)}, ${Math.max(0, secondaryRgb.b - 30)}, 1)`);
  }
};

export function useClub() {
  const context = useContext(ClubContext);
  if (!context) {
    throw new Error('useClub must be used within a ClubProvider');
  }
  return context;
}

// Grace period in days before suspension
const GRACE_PERIOD_DAYS = 7;

export function ClubProvider({ children }) {
  const [club, setClub] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine club from subdomain or fallback
  useEffect(() => {
    const detectClub = async () => {
      try {
        const hostname = window.location.hostname;
        let subdomain = null;

        // Extract subdomain from hostname
        // Supports: 
        //   - clublinktt.com (main domain - use ?club= param)
        //   - proseriesfc.clublinktt.com (subdomain routing)
        //   - localhost (dev)
        // Get club param - try multiple methods for Safari compatibility
        let clubParam = null;
        try {
          const params = new URLSearchParams(window.location.search);
          clubParam = params.get('club');
        } catch (e) {
          // Fallback for older Safari
          const match = window.location.search.match(/[?&]club=([^&]*)/);
          clubParam = match ? decodeURIComponent(match[1]) : null;
        }
        console.log('ClubContext: URL search:', window.location.search, 'clubParam:', clubParam);
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          // Development: check URL params or use default
          subdomain = clubParam || 'proseriesfc';
        } else if (hostname === 'clublinktt.com' || hostname === 'www.clublinktt.com') {
          // Main domain: use ?club= param (landing page handles no param)
          subdomain = clubParam;
        } else if (hostname.endsWith('.clublinktt.com')) {
          // Subdomain routing: proseriesfc.clublinktt.com
          const parts = hostname.split('.');
          subdomain = parts[0];
          if (subdomain === 'www') {
            subdomain = clubParam; // www.clublinktt.com uses param
          }
        } else if (hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com')) {
          // Firebase default domains: use ?club= param
          subdomain = clubParam;
        } else {
          // Other domains: try subdomain extraction
          const parts = hostname.split('.');
          if (parts.length >= 3) {
            subdomain = parts[0];
            if (subdomain === 'www') {
              subdomain = clubParam;
            }
          } else {
            subdomain = clubParam;
          }
        }

        if (!subdomain) {
          setError('Unable to determine club');
          setLoading(false);
          return;
        }

        const clubSubdomain = subdomain.toLowerCase();
        console.log('ClubContext: Looking for club:', clubSubdomain);

        // Try to find by document ID first (faster, no query needed)
        const clubDoc = await getDoc(doc(db, 'clubs', clubSubdomain));
        
        if (clubDoc.exists()) {
          console.log('ClubContext: Found club by ID:', clubDoc.id);
          setClub({ id: clubDoc.id, ...clubDoc.data() });
          setClubId(clubDoc.id);
        } else {
          // Fallback: query by subdomain field
          console.log('ClubContext: Not found by ID, querying by subdomain field');
          const clubsQuery = query(
            collection(db, 'clubs'),
            where('subdomain', '==', clubSubdomain)
          );
          const clubsSnap = await getDocs(clubsQuery);
          
          if (!clubsSnap.empty) {
            const clubData = clubsSnap.docs[0];
            console.log('ClubContext: Found club by subdomain query:', clubData.id);
            setClub({ id: clubData.id, ...clubData.data() });
            setClubId(clubData.id);
          } else {
            console.log('ClubContext: Club not found');
            setError(`Club "${subdomain}" not found`);
          }
        }
      } catch (err) {
        console.error('Error detecting club:', err);
        setError('Error loading club');
      } finally {
        setLoading(false);
      }
    };

    detectClub();
  }, []);

  // Apply club colors when club data changes
  useEffect(() => {
    if (club) {
      applyClubColors(club.primaryColor, club.secondaryColor);
    }
  }, [club]);

  // Subscription status helpers
  const getSubscriptionStatus = () => {
    if (!club?.subscription) return 'unknown';
    
    const { status, currentPeriodEnd } = club.subscription;
    
    // If manually set to cancelled or suspended, respect that
    if (status === 'cancelled' || status === 'suspended') {
      return status;
    }
    
    // Calculate based on currentPeriodEnd
    if (currentPeriodEnd) {
      const endDate = currentPeriodEnd.toDate ? currentPeriodEnd.toDate() : new Date(currentPeriodEnd);
      const now = new Date();
      const daysPastDue = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
      
      if (daysPastDue < 0) {
        // Still within paid period
        return 'active';
      } else if (daysPastDue <= GRACE_PERIOD_DAYS) {
        // Within grace period
        return 'past_due';
      } else {
        // Past grace period - suspended
        return 'suspended';
      }
    }
    
    return status || 'unknown';
  };

  const getDaysUntilDue = () => {
    if (!club?.subscription?.currentPeriodEnd) return null;
    
    const endDate = club.subscription.currentPeriodEnd.toDate 
      ? club.subscription.currentPeriodEnd.toDate() 
      : new Date(club.subscription.currentPeriodEnd);
    const now = new Date();
    const days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getDaysOverdue = () => {
    if (!club?.subscription?.currentPeriodEnd) return 0;
    
    const endDate = club.subscription.currentPeriodEnd.toDate 
      ? club.subscription.currentPeriodEnd.toDate() 
      : new Date(club.subscription.currentPeriodEnd);
    const now = new Date();
    const days = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const isSubscriptionActive = () => {
    return getSubscriptionStatus() === 'active';
  };

  const isSubscriptionPastDue = () => {
    return getSubscriptionStatus() === 'past_due';
  };

  const isSubscriptionSuspended = () => {
    return getSubscriptionStatus() === 'suspended';
  };

  const isSubscriptionCancelled = () => {
    return getSubscriptionStatus() === 'cancelled';
  };

  const value = {
    club,
    clubId,
    loading,
    error,
    // Helper to check if user has access to this club
    hasAccess: (userClubId, userRole) => {
      if (userRole === 'super_admin') return true;
      return userClubId === clubId;
    },
    // Subscription helpers
    subscription: club?.subscription || null,
    getSubscriptionStatus,
    getDaysUntilDue,
    getDaysOverdue,
    isSubscriptionActive,
    isSubscriptionPastDue,
    isSubscriptionSuspended,
    isSubscriptionCancelled,
    GRACE_PERIOD_DAYS
  };

  return (
    <ClubContext.Provider value={value}>
      {children}
    </ClubContext.Provider>
  );
}

export default ClubContext;
